import { useCallback, useEffect, useReducer, useRef } from 'react';

import { processAudioToText } from '../effects/processAudioToText';
import {
	voiceDictationReducer,
	VoiceDictationResultAction,
	type VoiceDictationState,
} from '../effects/voiceDictationReducer';

export const useVoiceDictation = (
	onTranscriptUpdate: (transcript: string) => void,
	audioBitsPerSecond = 32000,
	sampleRate = 16000,
	timeSlice = 3000,
): [VoiceDictationState, { startRecording: () => void; stopRecording: () => void }] => {
	const mediaStreamRef = useRef<MediaStream | null>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const audioChunksRef = useRef<Blob[]>([]);
	const lastProcessedPositionRef = useRef<number>(0);

	const [state, dispatch] = useReducer(voiceDictationReducer, {
		action: VoiceDictationResultAction.Uninitialized,
		transcription: null,
	});

	useEffect(() => {
		if (state.transcription) {
			onTranscriptUpdate(state.transcription);
		}
	}, [state.transcription, onTranscriptUpdate]);

	const onDataAvailable = useCallback(
		async (event: BlobEvent) => {
			audioChunksRef.current.push(event.data);

			try {
				const { transcription, newAudioEndPosition } = await processAudioToText(
					audioChunksRef.current,
					lastProcessedPositionRef.current,
				);

				lastProcessedPositionRef.current = newAudioEndPosition;

				dispatch({
					type: 'UPDATE_TRANSCRIPTION',
					payload: { nextTranscription: transcription.text },
				});
			} catch (error) {
				dispatch({
					type: 'SET_ERROR',
					payload: {
						error: error instanceof Error ? error.message : 'Unknown error',
					},
				});
			}
		},
		[dispatch],
	);

	const onError = useCallback(
		(event: MediaRecorderEventMap['error']) => {
			dispatch({
				type: 'SET_ERROR',
				payload: { error: event.message },
			});
		},
		[dispatch],
	);

	const onStop = useCallback(() => {
		dispatch({ type: 'SET_STOPPED' });

		if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach((track) => track.stop());
		}

		audioChunksRef.current = [];
	}, [dispatch]);

	const initializeMediaRecorder = useCallback(
		(stream: MediaStream) => {
			mediaStreamRef.current = stream;

			audioChunksRef.current = [];
			lastProcessedPositionRef.current = 0;

			// eslint-disable-next-line compat/compat
			const mediaRecorder = new MediaRecorder(stream, {
				audioBitsPerSecond,
				mimeType: 'audio/webm',
			});
			mediaRecorderRef.current = mediaRecorder;

			dispatch({ type: 'SET_RECORDING', payload: { transcription: '' } });

			mediaRecorder.start(timeSlice);

			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			mediaRecorder.addEventListener('dataavailable', onDataAvailable);

			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			mediaRecorder.addEventListener('stop', onStop);

			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			mediaRecorder.addEventListener('error', onError);
		},
		[audioBitsPerSecond, onDataAvailable, onError, onStop, timeSlice, dispatch],
	);

	const startRecording = useCallback(() => {
		dispatch({ type: 'SET_INITIALIZING' });

		navigator.mediaDevices
			.getUserMedia({
				audio: {
					sampleRate,
					autoGainControl: true,
					echoCancellation: true,
					noiseSuppression: true,
				},
			})
			.then(initializeMediaRecorder);
	}, [sampleRate, initializeMediaRecorder, dispatch]);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current) {
			mediaRecorderRef.current.stop();
		}
	}, []);

	return [state, { startRecording, stopRecording }];
};
